import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import '@tylertech/forge/button';
import { ColorPickerField } from '../forms';
import type { TagValue, TagValueType, TagValueValidation } from '../../graphql/types';
import './TagForm.css';

export interface TagFormCategory {
  entityId: string;
  name: string;
  scope: 'global' | 'team';
  scopeOwnerId?: string | null;
  /** Custom-field type for this category (issue #184); absent ⇒ label-only. */
  valueType?: TagValueType | null;
  validation?: TagValueValidation | null;
}

export interface TagFormData {
  name: string;
  categoryId: string;
  color?: string;
  description?: string;
  /** Typed value for non-label-only categories; coerced from the input on submit. */
  value?: TagValue;
}

/** Internal react-hook-form shape — the value input is always a raw string. */
interface TagFormFields {
  name: string;
  categoryId: string;
  color?: string;
  description?: string;
  value?: string;
}

/** Coerce a raw string input into the scalar the category's valueType expects. */
export function coerceTagValue(raw: string | undefined, valueType: TagValueType): TagValue {
  switch (valueType) {
    case 'number':
      return Number(raw);
    case 'boolean':
      return raw === 'true';
    default:
      // string | date | url are all carried as strings
      return raw ?? '';
  }
}

/** Map the public form data (typed value) into the internal string-based fields. */
function toFields(data?: Partial<TagFormData>): TagFormFields | undefined {
  if (!data) return undefined;
  const { value, ...rest } = data;
  return {
    ...rest,
    value: value === undefined || value === null ? '' : String(value),
  } as TagFormFields;
}

/** HTML input type for a given custom-field value type. */
function inputTypeFor(valueType: TagValueType): string {
  switch (valueType) {
    case 'number':
      return 'number';
    case 'date':
      return 'date';
    case 'url':
      return 'url';
    default:
      return 'text';
  }
}

export interface TagFormProps {
  defaultValues?: Partial<TagFormData>;
  categories?: TagFormCategory[];
  onSubmit: (data: TagFormData) => void | Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  mode: 'create' | 'edit';
}

export function TagForm({
  defaultValues,
  categories = [],
  onSubmit,
  onCancel,
  isSubmitting = false,
  mode,
}: TagFormProps): JSX.Element {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<TagFormFields>({
    defaultValues: toFields(defaultValues),
  });

  useEffect(() => {
    if (defaultValues) reset(toFields(defaultValues));
  }, [defaultValues, reset]);

  const ForgeButton = 'forge-button' as React.ElementType;
  const isEditMode = mode === 'edit';
  const noCategories = !isEditMode && categories.length === 0;

  const selectedCategoryId = watch('categoryId');
  const selectedCategory = categories.find((c) => c.entityId === selectedCategoryId);
  const valueType: TagValueType = selectedCategory?.valueType ?? 'label-only';
  const isTyped = valueType !== 'label-only';

  const submit = (fields: TagFormFields): void | Promise<void> => {
    const { value: rawValue, ...rest } = fields;
    const data: TagFormData = {
      ...rest,
      value: isTyped ? coerceTagValue(rawValue, valueType) : undefined,
    };
    return onSubmit(data);
  };

  return (
    <form className="tag-form" onSubmit={handleSubmit(submit)}>
      {noCategories && (
        <div className="tag-form__no-categories" role="alert">
          No tag categories are available to you. Ask an Administrator to create a global category,
          or become a primary contact for a team with an existing category.
        </div>
      )}

      <div className="tag-form__field">
        <label htmlFor="tag-category">Category</label>
        <select
          id="tag-category"
          aria-label="Category"
          disabled={noCategories || isEditMode}
          {...register('categoryId', { required: isEditMode ? false : 'Category is required' })}
        >
          <option value="">Select a category</option>
          {categories.map((cat) => (
            <option key={cat.entityId} value={cat.entityId}>
              {cat.name} {cat.scope === 'global' ? '(global)' : '(team)'}
            </option>
          ))}
        </select>
        {errors.categoryId && (
          <span className="tag-form__error" role="alert">
            {errors.categoryId.message}
          </span>
        )}
      </div>

      <div className="tag-form__field">
        <label htmlFor="tag-name">Tag Name</label>
        <input
          id="tag-name"
          type="text"
          aria-label="Tag Name"
          {...register('name', { required: 'Name is required' })}
        />
        {errors.name && (
          <span className="tag-form__error" role="alert">
            {errors.name.message}
          </span>
        )}
      </div>

      <div className="tag-form__field">
        <label htmlFor="tag-color">Color</label>
        <ColorPickerField
          id="tag-color"
          ariaLabel="Color"
          value={watch('color')}
          onChange={(hex) => setValue('color', hex, { shouldValidate: true })}
        />
      </div>

      <div className="tag-form__field">
        <label htmlFor="tag-description">Description</label>
        <textarea
          id="tag-description"
          aria-label="Description"
          rows={3}
          {...register('description')}
        />
      </div>

      {isTyped && (
        <div className="tag-form__field">
          <label htmlFor="tag-value">
            Value
            {selectedCategory ? ` (${valueType})` : ''}
          </label>
          {valueType === 'boolean' ? (
            <select
              id="tag-value"
              aria-label="Value"
              {...register('value', { required: 'Value is required' })}
            >
              <option value="">Select a value</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          ) : (
            <input
              id="tag-value"
              type={inputTypeFor(valueType)}
              aria-label="Value"
              step={valueType === 'number' ? 'any' : undefined}
              min={
                valueType === 'number'
                  ? (selectedCategory?.validation?.min ?? undefined)
                  : undefined
              }
              max={
                valueType === 'number'
                  ? (selectedCategory?.validation?.max ?? undefined)
                  : undefined
              }
              {...register('value', { required: 'Value is required' })}
            />
          )}
          {errors.value && (
            <span className="tag-form__error" role="alert">
              {errors.value.message}
            </span>
          )}
        </div>
      )}

      <div className="tag-form__actions">
        <ForgeButton type="button" variant="outlined" onClick={onCancel}>
          Cancel
        </ForgeButton>
        <ForgeButton type="submit" variant="raised" disabled={isSubmitting || noCategories}>
          {mode === 'create' ? 'Create Tag' : 'Save'}
        </ForgeButton>
      </div>
    </form>
  );
}
